import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router";
import { useUser } from "@clerk/clerk-react";
import { useQuery } from "@tanstack/react-query";
import { formatDistanceToNow } from "date-fns";
import {
  ArrowRight,
  ArrowUp,
  Building2,
  Clock,
  Megaphone,
  MessageSquare,
  Search,
} from "lucide-react";
import { raiseBoardApi, type RaiseBoardSummary } from "@/api/raise-board-api";
import { BoardAuthDialog } from "@/components/auth/board-auth-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { CompanyAvatar } from "./company-avatar";
import { HeroStat } from "./board-row";
import { MetaRow, RBCard, RaiseBoardNav, RaiseBoardShell } from "./rb-ui";

/*
 * /rb — the Raiseboard directory.
 *
 * Every published board ranked by the three numbers a visitor actually cares
 * about: deals listed, upvotes earned, comments written. /global ranks
 * *companies* across all boards; this ranks the boards themselves, and is the
 * way into a specific /rb/:slug for someone who arrived without a link.
 */

type SortKey = "deals" | "upvotes" | "comments" | "recent";

const SORTS: Array<{ key: SortKey; label: string }> = [
  { key: "deals", label: "Deals" },
  { key: "upvotes", label: "Upvotes" },
  { key: "comments", label: "Comments" },
  { key: "recent", label: "Recently active" },
];

/** The metric the meter bar measures. "recent" has no magnitude to show. */
const METRIC: Record<SortKey, ((b: RaiseBoardSummary) => number) | null> = {
  deals: (b) => b.deal_count,
  upvotes: (b) => b.upvote_count,
  comments: (b) => b.comment_count,
  recent: null,
};

function compactNumber(value: number): string {
  if (value < 1000) return String(value);
  return `${(value / 1000).toFixed(1).replace(/\.0$/, "")}k`;
}

function timeAgo(value: string | null): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return formatDistanceToNow(date, { addSuffix: true });
}

/** One number in a board row. Columns of figures, so tabular digits. */
function BoardStat({
  icon: Icon,
  value,
  label,
  emphasis = false,
}: {
  icon: typeof Building2;
  value: number;
  label: string;
  /** The column currently being ranked on — the rest recede. */
  emphasis?: boolean;
}) {
  return (
    <div className="text-right">
      <p
        className={cn(
          "text-lg font-semibold leading-none tabular-nums",
          emphasis ? "text-foreground" : "text-foreground/70"
        )}
      >
        {compactNumber(value)}
      </p>
      <p className="mt-1.5 inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">
        <Icon className="h-3 w-3 shrink-0" />
        {label}
      </p>
    </div>
  );
}

function BoardRankRow({
  board,
  rank,
  sort,
  share,
}: {
  board: RaiseBoardSummary;
  rank: number;
  sort: SortKey;
  /** This board's value as a fraction of the leader's, on the active metric. */
  share: number | null;
}) {
  const added = timeAgo(board.last_deal_added);

  return (
    <div className="group relative rounded-2xl border border-border bg-card p-4 shadow-sm transition-colors hover:border-foreground/20 sm:p-5">
      <Link
        to={`/rb/${board.slug}`}
        aria-label={`Open the ${board.name} Raiseboard`}
        className="absolute inset-0 z-0 rounded-2xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/40"
      />

      <div className="pointer-events-none relative z-10 flex items-start gap-3 sm:gap-4">
        {/* The leader is filled, the podium outlined, everyone else plain —
            rank without introducing a second color into the board skin. */}
        <span
          className={cn(
            "mt-0.5 hidden h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-semibold tabular-nums sm:inline-flex",
            rank === 1
              ? "bg-foreground text-background"
              : rank <= 3
                ? "border border-foreground/30 text-foreground"
                : "text-muted-foreground/70"
          )}
        >
          {rank}
        </span>

        <CompanyAvatar
          size="xl"
          tone="soft"
          logoUrl={board.logo_url ?? undefined}
          name={board.name}
        />

        <div className="min-w-0 flex-1">
          <h3 className="font-serif text-xl leading-tight">{board.name}</h3>

          {board.description && (
            <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">
              {board.description}
            </p>
          )}

          {/* Share of the leader on the active metric. One hue: the track is
              the same ink at low opacity, so the bar reads as one object. */}
          {share !== null && (
            <div
              className="mt-3 h-1.5 w-full max-w-[280px] overflow-hidden rounded-full bg-foreground/10"
              role="presentation"
            >
              <div
                className="h-full rounded-full bg-foreground/70"
                style={{ width: `${Math.max(share * 100, 2)}%` }}
              />
            </div>
          )}

          <MetaRow
            className="mt-2.5 text-xs"
            items={[
              added && (
                <span className="inline-flex items-center gap-1">
                  <Clock className="h-3 w-3 shrink-0" />
                  Newest deal {added}
                </span>
              ),
              board.global_deal_count > 0 && (
                <span>{board.global_deal_count} on the global board</span>
              ),
              <span className="text-foreground/60">/rb/{board.slug}</span>,
            ]}
          />

          {/* Stats stack under the name on narrow screens, where the right
              rail has no room. */}
          <div className="mt-4 flex items-end gap-6 sm:hidden">
            <BoardStat
              icon={Building2}
              value={board.deal_count}
              label="Deals"
              emphasis={sort === "deals"}
            />
            <BoardStat
              icon={ArrowUp}
              value={board.upvote_count}
              label="Upvotes"
              emphasis={sort === "upvotes"}
            />
            <BoardStat
              icon={MessageSquare}
              value={board.comment_count}
              label="Comments"
              emphasis={sort === "comments"}
            />
          </div>
        </div>

        <div className="hidden shrink-0 items-end gap-7 pt-1 sm:flex">
          <BoardStat
            icon={Building2}
            value={board.deal_count}
            label="Deals"
            emphasis={sort === "deals"}
          />
          <BoardStat
            icon={ArrowUp}
            value={board.upvote_count}
            label="Upvotes"
            emphasis={sort === "upvotes"}
          />
          <BoardStat
            icon={MessageSquare}
            value={board.comment_count}
            label="Comments"
            emphasis={sort === "comments"}
          />
        </div>
      </div>
    </div>
  );
}

export default function RaiseBoardDirectory() {
  const navigate = useNavigate();
  const { isSignedIn } = useUser();

  const [query, setQuery] = useState("");
  const [sort, setSort] = useState<SortKey>("deals");
  const [authReason, setAuthReason] = useState<string | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ["raise-boards"],
    queryFn: () => raiseBoardApi.getBoards(),
    retry: false,
    refetchOnWindowFocus: true,
  });

  const boards = useMemo(() => data ?? [], [data]);

  const totals = useMemo(
    () =>
      boards.reduce(
        (acc, b) => ({
          deals: acc.deals + b.deal_count,
          upvotes: acc.upvotes + b.upvote_count,
          comments: acc.comments + b.comment_count,
        }),
        { deals: 0, upvotes: 0, comments: 0 }
      ),
    [boards]
  );

  const ranked = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const matched = needle
      ? boards.filter(
          (b) =>
            b.name.toLowerCase().includes(needle) ||
            b.slug.toLowerCase().includes(needle) ||
            (b.description ?? "").toLowerCase().includes(needle)
        )
      : boards;

    const metric = METRIC[sort];
    return [...matched].sort((a, b) => {
      if (!metric) {
        // Recently active: boards that have never dated a deal sort last.
        const at = a.last_deal_added ? Date.parse(a.last_deal_added) : 0;
        const bt = b.last_deal_added ? Date.parse(b.last_deal_added) : 0;
        return bt - at || b.deal_count - a.deal_count;
      }
      return metric(b) - metric(a) || b.deal_count - a.deal_count;
    });
  }, [boards, query, sort]);

  // The leader's value sets the meter's full width, so bars compare against
  // the top of the current ranking rather than an arbitrary ceiling.
  const leaderValue = useMemo(() => {
    const metric = METRIC[sort];
    if (!metric || ranked.length === 0) return 0;
    return Math.max(...ranked.map(metric));
  }, [ranked, sort]);

  if (error) {
    return (
      <RaiseBoardShell>
        <RaiseBoardNav onRequestAuth={setAuthReason} />
        <div className="flex flex-col items-center justify-center gap-3 px-4 py-32 text-center">
          <Megaphone className="h-10 w-10 text-muted-foreground/40" />
          <p className="font-serif text-2xl">The board directory isn't available</p>
          <p className="max-w-sm text-sm text-muted-foreground">
            Please try again in a moment.
          </p>
        </div>
      </RaiseBoardShell>
    );
  }

  return (
    <RaiseBoardShell>
      <RaiseBoardNav onRequestAuth={setAuthReason} />

      {/* Masthead: the same inverted band the global board leads with, so the
          two public surfaces read as one product. */}
      <div className="border-b border-border/40 bg-foreground text-background">
        <div className="mx-auto flex max-w-6xl flex-col gap-8 px-4 py-10 sm:px-6 lg:flex-row lg:items-center lg:justify-between lg:py-12">
          <div className="max-w-xl">
            <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-background/50">
              Powered by DiligenceGPT
            </p>
            <h2 className="mt-3 font-serif text-3xl leading-tight sm:text-4xl">
              Every Raiseboard, ranked.
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-background/70">
              Funds, accelerators and communities publishing the startups they
              back — ordered by how much is on the board and how loudly the
              community is backing it.
            </p>
          </div>

          <div className="flex flex-col items-start gap-5 lg:items-end lg:border-l lg:border-background/15 lg:pl-10">
            <div
              role="group"
              aria-label="Raiseboard totals"
              className="flex items-start gap-8"
            >
              <HeroStat value={boards.length} label="Boards" />
              <HeroStat value={totals.deals} label="Deals" />
              <HeroStat value={totals.upvotes} label="Upvotes" />
              <HeroStat value={totals.comments} label="Comments" />
            </div>
            <button
              type="button"
              onClick={() => navigate("/global")}
              className="inline-flex items-center gap-1.5 text-sm font-medium text-background/70 transition-colors hover:text-background"
            >
              Browse every deal on the global board
              <ArrowRight className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      </div>

      <main className="mx-auto max-w-6xl px-4 py-8 sm:px-6 sm:py-10">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-red-600 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.12em] text-white">
          <span className="h-1.5 w-1.5 rounded-full bg-white" />
          Live
        </span>
        <h1 className="mt-4 font-serif text-5xl leading-none sm:text-6xl">
          Raiseboard leaderboard
        </h1>
        <p className="mt-3 border-l-2 border-border pl-3 text-sm text-muted-foreground">
          {isLoading ? (
            "Loading the leaderboard…"
          ) : (
            <>
              {ranked.length} board{ranked.length === 1 ? "" : "s"}
              {query.trim() ? ` of ${boards.length}` : ""} · Ranked by{" "}
              {SORTS.find((s) => s.key === sort)?.label.toLowerCase()}
            </>
          )}
        </p>

        {/* Controls in one row above the list: search on the left, the ranking
            column on the right. */}
        <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="relative w-full sm:max-w-xs">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search boards"
              aria-label="Search boards"
              className="h-10 rounded-full border-border bg-card pl-9"
            />
          </div>

          <div
            role="group"
            aria-label="Sort boards"
            className="flex flex-wrap items-center gap-1 rounded-full border border-border bg-card p-1"
          >
            {SORTS.map((option) => (
              <button
                key={option.key}
                type="button"
                aria-pressed={sort === option.key}
                onClick={() => setSort(option.key)}
                className={cn(
                  "rounded-full px-3 py-1.5 text-xs font-medium transition-colors",
                  sort === option.key
                    ? "bg-foreground text-background"
                    : "text-muted-foreground hover:text-foreground"
                )}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-6 space-y-3">
          {isLoading ? (
            [0, 1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-[128px] w-full rounded-2xl" />
            ))
          ) : ranked.length === 0 ? (
            <RBCard className="py-12 text-center">
              <Megaphone className="mx-auto h-8 w-8 text-muted-foreground/40" />
              <p className="mt-3 text-sm text-muted-foreground">
                {boards.length === 0
                  ? "No boards are publishing yet."
                  : `No boards match “${query.trim()}”.`}
              </p>
            </RBCard>
          ) : (
            ranked.map((board, index) => {
              const metric = METRIC[sort];
              return (
                <BoardRankRow
                  key={board.slug}
                  board={board}
                  rank={index + 1}
                  sort={sort}
                  share={
                    metric && leaderValue > 0
                      ? metric(board) / leaderValue
                      : null
                  }
                />
              );
            })
          )}
        </div>

        <RBCard className="mt-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="font-serif text-xl">Run your own Raiseboard</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Publish the startups you back and let your community endorse them.
            </p>
          </div>
          <Button
            onClick={() =>
              isSignedIn
                ? navigate("/workspaces")
                : setAuthReason("Sign in to run your own Raiseboard.")
            }
            className="shrink-0 rounded-full px-5"
          >
            Get started free
            <ArrowRight className="h-4 w-4" />
          </Button>
        </RBCard>
      </main>

      <BoardAuthDialog
        open={!!authReason}
        onOpenChange={(open) => {
          if (!open) setAuthReason(null);
        }}
        reason={authReason ?? undefined}
      />
    </RaiseBoardShell>
  );
}
